import React, { useEffect, useState } from "react";
import { friendlyError } from "../../../middleware/common-functions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import {
  GET_SUPPLIERS,
  GET_BRANCHES,
  GET_INVENTORY_INGREDIENTS,
  CREATE_PURCHASE_ORDER,
} from "./queries";
import { UNIT_OPTIONS, InventoryIngredient } from "./models";

interface ItemRow {
  key: number;
  ingredient_id: string;
  ingredientName: string;
  quantity_ordered: string;
  unit: string;
  unit_price: string;
  notes: string;
}

const emptyRow = (key: number): ItemRow => ({
  key,
  ingredient_id: "",
  ingredientName: "",
  quantity_ordered: "",
  unit: "pz",
  unit_price: "",
  notes: "",
});

const PurchaseOrderForm: React.FC = () => {
  const history = useHistory();

  const [supplierId, setSupplierId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");

  const [rowKey, setRowKey] = useState(1);
  const [rows, setRows] = useState<ItemRow[]>([emptyRow(0)]);

  // Ingredient search
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  const [fetchSuppliers, { data: suppliersData }] = useLazyQuery(
    GET_SUPPLIERS,
    { fetchPolicy: "no-cache" }
  );
  const [fetchBranches, { data: branchesData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });
  const [fetchIngredients, { data: ingredientsData }] = useLazyQuery(
    GET_INVENTORY_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );

  const [createPurchaseOrder] = useMutation(CREATE_PURCHASE_ORDER);

  useEffect(() => {
    fetchSuppliers({ variables: { take: 200 } });
    fetchBranches();
    fetchIngredients({ variables: { take: 500 } });
    //eslint-disable-next-line
  }, []);

  const suppliers = suppliersData?.getSuppliers || [];
  const branches = branchesData?.getBranches || [];
  const ingredients: InventoryIngredient[] =
    ingredientsData?.getInventoryIngredients || [];

  const filteredIngredients = ingredientSearch
    ? ingredients.filter((i) =>
        i.name.toLowerCase().includes(ingredientSearch.toLowerCase())
      )
    : ingredients;

  // Auto-fill price from supplier-ingredient link
  const selectedSupplier = suppliers.find(
    (s: any) => s.id === supplierId
  );

  const selectIngredient = (ing: InventoryIngredient, index: number) => {
    const updated = [...rows];
    updated[index] = {
      ...updated[index],
      ingredient_id: ing.id,
      ingredientName: ing.name,
      unit: ing.unit,
    };
    // Try to auto-fill price from supplier link
    if (selectedSupplier?.ingredients) {
      const link = selectedSupplier.ingredients.find(
        (si: any) => si.ingredient_id.toString() === ing.id
      );
      if (link) {
        updated[index].unit_price = link.unit_price?.toString() || "";
        updated[index].unit = link.unit || ing.unit;
      }
    }
    setRows(updated);
    setIngredientSearch("");
    setActiveRowIndex(null);
  };

  const updateRow = (index: number, field: string, value: any) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const addRow = () => {
    setRows([...rows, emptyRow(rowKey)]);
    setRowKey(rowKey + 1);
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  // Totals
  const subtotal = rows.reduce((sum, r) => {
    const qty = parseFloat(r.quantity_ordered) || 0;
    const price = parseFloat(r.unit_price) || 0;
    return sum + qty * price;
  }, 0);
  const tax = subtotal * 0.16;
  const total = subtotal + tax;

  const handleSave = async (sendToSupplier: boolean) => {
    if (!supplierId) {
      toast.error(lang("Selecciona un proveedor", "Select a supplier"));
      return;
    }
    if (!branchId) {
      toast.error(lang("Selecciona una sucursal", "Select a branch"));
      return;
    }
    const validRows = rows.filter(
      (r) => r.ingredient_id && r.quantity_ordered && r.unit_price
    );
    if (validRows.length === 0) {
      toast.error(
        lang("Agrega al menos un producto", "Add at least one item")
      );
      return;
    }

    const items = validRows.map((r) => ({
      ingredient_id: parseInt(r.ingredient_id),
      quantity_ordered: parseFloat(r.quantity_ordered),
      unit: r.unit,
      unit_price: parseFloat(r.unit_price),
      notes: r.notes || undefined,
    }));

    try {
      const result = await createPurchaseOrder({
        variables: {
          supplier_id: parseInt(supplierId),
          branch_id: parseInt(branchId),
          expected_date: expectedDate || null,
          notes: notes || null,
          items,
        },
      });
      const poId = result.data.createPurchaseOrder.id;

      if (sendToSupplier) {
        // Update status to "sent" — handled via updatePurchaseOrder
        // For now, just redirect to detail
        toast.success(lang("Orden creada y enviada", "Order created and sent"));
      } else {
        toast.success(lang("Orden guardada como borrador", "Order saved as draft"));
      }
      history.push(`/inventory/purchases/${poId}`);
    } catch (err: any) {
      toast.error(friendlyError(err));
    }
  };

  return (
    <>
      <p>
        <Link to="/inventory/purchases">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Órdenes de Compra", "Purchase Orders")}
        </Link>
      </p>
      <h1>{lang("Nueva Orden de Compra", "New Purchase Order")}</h1>

      <div className="row">
        {/* Left: PO info */}
        <div className="col-lg-4">
          <div className="material_card p-3 mb-3">
            <h4>{lang("Datos de la orden", "Order details")}</h4>
            <div className="form-group">
              <label>{lang("Proveedor", "Supplier")} *</label>
              <select
                className="custom-select"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              >
                <option value="">
                  {lang("Seleccionar...", "Select...")}
                </option>
                {suppliers.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{lang("Sucursal", "Branch")} *</label>
              <select
                className="custom-select"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
              >
                <option value="">
                  {lang("Seleccionar...", "Select...")}
                </option>
                {branches.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{lang("Fecha entrega esperada", "Expected delivery date")}</label>
              <input
                type="date"
                className="form-control"
                value={expectedDate}
                onChange={(e) => setExpectedDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{lang("Notas", "Notes")}</label>
              <textarea
                className="form-control"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Totals card */}
          <div className="material_card p-3 mb-3">
            <h4>{lang("Totales", "Totals")}</h4>
            <table className="table table-sm mb-0">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td className="text-right">${subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>IVA (16%)</td>
                  <td className="text-right">${tax.toFixed(2)}</td>
                </tr>
                <tr className="font-weight-bold">
                  <td>Total</td>
                  <td className="text-right">${total.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between">
            <button
              className="btn btn-outline-primary"
              onClick={() => handleSave(false)}
            >
              <i className="far fa-save"></i>{" "}
              {lang("Guardar Borrador", "Save Draft")}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => handleSave(true)}
            >
              <i className="fas fa-paper-plane"></i>{" "}
              {lang("Enviar a Proveedor", "Send to Supplier")}
            </button>
          </div>
        </div>

        {/* Right: Item rows */}
        <div className="col-lg-8">
          <div className="material_card p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">
                {lang("Productos a ordenar", "Items to order")}
              </h4>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={addRow}
              >
                <i className="fa fa-plus"></i> {lang("Agregar", "Add")}
              </button>
            </div>

            {!supplierId && (
              <div className="alert alert-info">
                <small>
                  {lang(
                    "Selecciona un proveedor para autocompletar precios",
                    "Select a supplier to auto-fill prices"
                  )}
                </small>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-sm">
                <thead className="thead-light">
                  <tr>
                    <th style={{ minWidth: 200 }}>
                      {lang("Ingrediente", "Ingredient")}
                    </th>
                    <th style={{ width: 100 }}>
                      {lang("Cantidad", "Qty")}
                    </th>
                    <th style={{ width: 100 }}>
                      {lang("Unidad", "Unit")}
                    </th>
                    <th style={{ width: 100 }}>
                      {lang("Precio/U", "Price/U")}
                    </th>
                    <th style={{ width: 100 }}>
                      {lang("Total", "Total")}
                    </th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const lineTotal =
                      (parseFloat(row.quantity_ordered) || 0) *
                      (parseFloat(row.unit_price) || 0);

                    return (
                      <tr key={row.key}>
                        <td className="position-relative">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={lang("Buscar...", "Search...")}
                            value={
                              activeRowIndex === index
                                ? ingredientSearch
                                : row.ingredientName
                            }
                            onChange={(e) => {
                              setIngredientSearch(e.target.value);
                              setActiveRowIndex(index);
                            }}
                            onFocus={() => {
                              setActiveRowIndex(index);
                              setIngredientSearch("");
                            }}
                            onBlur={() =>
                              setTimeout(() => setActiveRowIndex(null), 200)
                            }
                          />
                          {activeRowIndex === index && (
                            <div
                              className="list-group position-absolute w-100"
                              style={{
                                zIndex: 10,
                                maxHeight: 150,
                                overflowY: "auto",
                              }}
                            >
                              {filteredIngredients
                                .slice(0, 15)
                                .map((ing) => (
                                  <button
                                    key={ing.id}
                                    type="button"
                                    className="list-group-item list-group-item-action py-1"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() =>
                                      selectIngredient(ing, index)
                                    }
                                  >
                                    <small>
                                      {ing.name}{" "}
                                      <span className="text-muted">
                                        ({ing.unit})
                                      </span>
                                    </small>
                                  </button>
                                ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.quantity_ordered}
                            onChange={(e) =>
                              updateRow(index, "quantity_ordered", e.target.value)
                            }
                            step="0.01"
                            min="0"
                          />
                        </td>
                        <td>
                          <select
                            className="custom-select custom-select-sm"
                            value={row.unit}
                            onChange={(e) =>
                              updateRow(index, "unit", e.target.value)
                            }
                          >
                            {UNIT_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.value}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.unit_price}
                            onChange={(e) =>
                              updateRow(index, "unit_price", e.target.value)
                            }
                            step="0.01"
                            min="0"
                          />
                        </td>
                        <td className="text-right">
                          <small>${lineTotal.toFixed(2)}</small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeRow(index)}
                            disabled={rows.length <= 1}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-weight-bold">
                    <td colSpan={4} className="text-right">
                      Subtotal
                    </td>
                    <td className="text-right">${subtotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderForm;
